import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getContactUs, removeContactUs, updateContactUs } from "../slice/contactUsSlice";
import AdminTable from "common/AdminTable";
import DateFilter from "common/DateFilter";
import TruncatedMessage from "common/TruncatedMessage";
import { toast } from "react-toastify";

const Contactus = () => {
  const dispatch = useDispatch();

  // Better naming (cartData confusing tha)
  const { data = [], loading, error } = useSelector((state) => state.contactus);
  const contacts = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const [showModal, setShowModal] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    subject: "",
    message: "",
    address: "",
    status: 0,
  });

  useEffect(() => {
    dispatch(getContactUs());
  }, [dispatch]);

  useEffect(() => {
    if (!showModal) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showModal]);

  useEffect(() => {
    if (!pendingUpdate) return;
    if (loading) return;

    if (!error) {
      setShowModal(false);
      setEditingContact(null);
    }
    setPendingUpdate(false);
  }, [pendingUpdate, loading, error]);

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      dispatch(removeContactUs(id));
    }
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact?.name ?? "",
      mobile: contact?.mobile ?? "",
      subject: contact?.subject ?? "",
      message: contact?.message ?? "",
      address: contact?.address ?? "",
      status: Number.isFinite(Number(contact?.status)) ? Number(contact.status) : 0,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setPendingUpdate(false);
  };

  const isBlank = (value) => value === undefined || value === null || String(value).trim() === "";

  const normalizeMobile = (value) => {
    const mobile = String(value ?? "").trim();
    if (!/^[0-9]{10}$/.test(mobile)) return null;
    return mobile;
  };

  const handleUpdate = () => {
    if (!editingContact?.id) return;

    const name = String(form.name ?? "").trim();
    if (isBlank(name)) return toast.error("Name is required.");

    const mobile = normalizeMobile(form.mobile);
    if (!mobile) return toast.error("Mobile number must be exactly 10 digits.");

    const statusValue = Number(form.status);
    if (!Number.isInteger(statusValue)) return toast.error("Status must be an integer.");

    setPendingUpdate(true);
    dispatch(
      updateContactUs({
        id: editingContact.id,
        data: {
          name,
          mobile,
          subject: isBlank(form.subject) ? null : String(form.subject),
          message: isBlank(form.message) ? null : String(form.message),
          address: isBlank(form.address) ? null : String(form.address),
          status: statusValue,
        },
      })
    );
  };

  const [filteredContacts, setFilteredContacts] = useState([]);
  const filterColumns = [
    { Header: 'ID', accessor: 'id' },
    { Header: 'Name', accessor: 'name' },
    { Header: 'Subject', accessor: 'subject' },
    { Header: 'Mobile', accessor: 'mobile' },
    { Header: 'Status', accessor: 'status' },
  ];

  const tableHeaders = [
    {
      Header: "ID",
      accessor: "id",
    },
    {
      Header: "Name",
      accessor: "name",
    },
    {
      Header: "Subject",
      accessor: "subject",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Mobile",
      accessor: "mobile",
    },
    {
      Header: "Message",
      accessor: "message",
      Cell: ({ row }) => (
        <TruncatedMessage
          text={row.original.message || ""}
          maxLength={30}
        />
      ),
    },
    {
      Header: "Address",
      accessor: "address",
      Cell: ({ value }) => value || "-",
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ value }) => (
        <span>
          {Number(value) === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center" style={{ gap: 8 }}>
          <button className="btn btn-sm btn-warning text-white" onClick={() => openEdit(row.original)}>
            Edit
          </button>
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row.original.id)}>
            Remove
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="text-white font-weight-bold">
          Contact List
        </h4>
      </div>

      {loading ? (
        <div className="text-white mx-4">Loading...</div>
      ) : contacts.length > 0 ? (
        <>
          <div className="mx-4">
            <DateFilter
              data={contacts}
              dateField="createdAt"
              title="Contact Enquiries Report"
              columns={filterColumns}
              onFilteredData={setFilteredContacts}
            />
          </div>
          <AdminTable
            tableHeaders={tableHeaders}
            tableData={filteredContacts}
          />
        </>
      ) : (
        <div className="text-white mx-4">
          No contact records found.
        </div>
      )}

      {showModal && editingContact && (
        <>
          <button
            type="button"
            className="modal-backdrop fade show border-0 p-0"
            aria-label="Close modal"
            onClick={closeModal}
          />
          <div className="modal fade show" style={{ display: "block" }} role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Contact</h5>
                  <button className="close" onClick={closeModal}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="admin-label" htmlFor="contact-name">Name</label>
                      <input
                        id="contact-name"
                        className="form-control"
                        value={form.name}
                        onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="admin-label" htmlFor="contact-mobile">Mobile</label>
                      <input
                        id="contact-mobile"
                        className="form-control"
                        inputMode="numeric"
                        value={form.mobile}
                        onChange={(e) => setForm((p) => ({ ...p, mobile: e.target.value }))}
                        placeholder="10 digit mobile"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="admin-label" htmlFor="contact-subject">Subject</label>
                      <input
                        id="contact-subject"
                        className="form-control"
                        value={form.subject}
                        onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="admin-label" htmlFor="contact-status">Status</label>
                      <select
                        id="contact-status"
                        className="form-control"
                        value={String(form.status)}
                        onChange={(e) => setForm((p) => ({ ...p, status: Number(e.target.value) }))}
                      >
                        <option value="0">Inactive</option>
                        <option value="1">Active</option>
                      </select>
                    </div>

                    <div className="col-12 mb-3">
                      <label className="admin-label" htmlFor="contact-address">Address</label>
                      <input
                        id="contact-address"
                        className="form-control"
                        value={form.address}
                        onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                      />
                    </div>

                    <div className="col-12 mb-3">
                      <label className="admin-label" htmlFor="contact-message">Message</label>
                      <textarea
                        id="contact-message"
                        rows={3}
                        className="form-control"
                        value={form.message}
                        onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                      />
                    </div>

                    {error && (
                      <div className="col-12">
                        <div className="alert alert-danger py-2 mb-0">{String(error)}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={closeModal} disabled={loading}>
                    Close
                  </button>
                  <button className="btn addbtn" onClick={handleUpdate} disabled={loading}>
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Contactus;
